import { StylesConfig, components, MultiValueProps } from 'react-select';

import {
  STATUS_COLOR_MAP,
  DEFAULT_STATUS_COLOR_SCHEME,
} from '../../../../constants/statusColors';
import { RosterRequestStatus } from '../../../../generated/graphql';
import { RequestStatusBadge } from '../../../Badge';

import TableFilterReactSelect, { Props } from '.';
// to handle an enum type (with label and value), some helper types and methods are useful
type EnumOption = {
  label: string;
  value: string;
};
// the property set in our form is an array of string values
type FieldValue = Array<EnumOption['value']>;
// the value passed to the select input is an array of options (with label and value)
type InputValue = Array<EnumOption>;

const CustomMultiValue = <
  Option extends EnumOption,
  IsMulti extends boolean = true,
>(
  props: MultiValueProps<Option, IsMulti>,
) => {
  // Check if this is a status field by looking at useStatusBadge prop or field name
  const selectProps = props.selectProps as {
    useStatusBadge?: boolean;
    name?: string;
    input?: { name?: string };
  };
  const isStatusField =
    selectProps.useStatusBadge === true ||
    (selectProps.useStatusBadge !== false &&
      (selectProps.name === 'status' || selectProps.input?.name === 'status'));

  if (isStatusField) {
    return (
      <components.MultiValue {...props}>
        <div className="mr-1 flex items-center">
          <RequestStatusBadge
            status={props.data.value as RosterRequestStatus}
          />
        </div>
      </components.MultiValue>
    );
  }

  // For non-status fields, render with default styling
  return <components.MultiValue {...props} />;
};

export function TableFilterReactSelectEnum(
  props: Props<InputValue, FieldValue, true> & { useStatusBadge?: boolean },
) {
  // Use the explicit useStatusBadge prop if provided, otherwise fall back to name check
  const isStatusField =
    props.useStatusBadge === true ||
    (props.useStatusBadge !== false &&
      (props.name === 'status' || props.input?.name === 'status'));

  const customStyles: Partial<StylesConfig<EnumOption, true>> = {
    option: (provided, { data }) => ({
      ...provided,
      backgroundColor: getStatusColor(data.value),
      color: getStatusTextColor(data.value),
      '&:hover': {
        backgroundColor: getStatusHoverColor(data.value),
      },
    }),
    multiValue: (provided) => ({
      ...provided,
      // Only make background transparent for status fields (which use badges)
      backgroundColor: isStatusField ? 'transparent' : provided.backgroundColor,
      marginRight: '0.25rem',
      marginTop: '0.25rem',
      marginBottom: '0.25rem',
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      paddingLeft: isStatusField ? 0 : provided.paddingLeft,
      paddingRight: isStatusField ? 0 : provided.paddingRight,
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: '0.5rem',
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: 'currentColor',
      paddingLeft: '0.25rem',
      paddingRight: '0.25rem',
      ':hover': {
        backgroundColor: isStatusField
          ? 'transparent'
          : provided[':hover']?.backgroundColor,
        opacity: 0.7,
      },
    }),
    // Asegurar que los colores se mantengan en mobile
    menuList: (provided) => ({
      ...provided,
      '@media (max-width: 640px)': {
        '& .select__option': {
          backgroundColor: 'inherit',
          color: 'inherit',
        },
      },
    }),
  };

  return (
    <TableFilterReactSelect
      {...props}
      isClearable={false}
      styles={customStyles as unknown as StylesConfig<InputValue, true>}
      components={{
        MultiValue: CustomMultiValue as typeof components.MultiValue,
      }}
    />
  );
}

/**
 * Get the background color for a given status.
 * Uses the shared status color mapping to ensure consistency with Badge component.
 * @param status - The roster request status
 * @returns The background color hex value
 */
export const getStatusColor = (status: string): string => {
  const colorScheme = STATUS_COLOR_MAP[status as RosterRequestStatus];
  return colorScheme?.background || DEFAULT_STATUS_COLOR_SCHEME.background;
};

/**
 * Get the text color for a given status.
 * Uses the shared status color mapping to ensure consistency with Badge component.
 * @param status - The roster request status
 * @returns The text color hex value
 */
export const getStatusTextColor = (status: string): string => {
  const colorScheme = STATUS_COLOR_MAP[status as RosterRequestStatus];
  return colorScheme?.text || DEFAULT_STATUS_COLOR_SCHEME.text;
};

/**
 * Get the hover background color for a given status.
 * Uses the shared status color mapping to ensure consistency with Badge component.
 * @param status - The roster request status
 * @returns The hover background color hex value
 */
export const getStatusHoverColor = (status: string): string => {
  const colorScheme = STATUS_COLOR_MAP[status as RosterRequestStatus];
  return colorScheme?.hover || DEFAULT_STATUS_COLOR_SCHEME.hover;
};

export function parseValuesToEnums(fieldValue: InputValue): FieldValue {
  return fieldValue.map((value) => value.value);
}

export function formatEnumsToOptions(options: InputValue) {
  return (values: FieldValue): InputValue => {
    if (!values) {
      return [];
    }
    if (!options || !Array.isArray(options)) {
      return [];
    }
    return (
      values
        .map((value) => {
          return options.find((option) => option.value === value);
        })
        // if for some reason we could find the option in the list, ignore it
        .filter((value) => value != null) as InputValue
    );
  };
}
